import React from 'react';

import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import SmartToyIcon from '@material-ui/icons/Toys'; // the icon of the AI panel button

import IProduct, { IAiFieldReview, IAiReview, IAiReviewRunSummary } from 'models/product';
import ConfidenceChip from './ConfidenceChip';
import AiReviewReasonPoints from './AiReviewReasonPoints';
import { aiVerdictChange, describeReviewedValue, TAiVerdictChange } from './aiReviewChanges';

export interface IAiFieldVerdict extends IAiFieldReview {
  // From the run of the review (superusers only); the product keeps reviews without the confidence.
  provider?: string | null;
  // Whether the field changed since the review, and the value the review saw.
  change?: TAiVerdictChange;
  reviewedValue?: unknown;
  // From before reviews were logged: the values the review saw are unknown, so it may be out of date.
  legacy?: boolean;
}

// The failed AI review verdicts by product field; the product form provides it to superusers only,
// so outside the form and for other roles the fields show nothing.
export const AiFieldVerdictContext = React.createContext<Record<string, IAiFieldVerdict>>({});

// current: the form now; saved: the product as saved. Both tell whether a failed field changed since the review.
export const failedAiVerdicts = (
  review?: IAiReview | null,
  runSummary?: Pick<IAiReviewRunSummary, 'provider' | 'confidences' | 'reviewedValues'> | null,
  current?: Partial<IProduct> | null,
  saved?: Partial<IProduct> | null
): Record<string, IAiFieldVerdict> =>
  Object.fromEntries(
    (review?.fieldReviews || [])
      .filter(item => item.status === 'failed')
      .map(item => [
        item.field,
        {
          ...item,
          confidence: runSummary?.confidences?.[item.field] ?? item.confidence ?? null,
          provider: runSummary?.provider ?? null,
          change: aiVerdictChange(item.field, runSummary?.reviewedValues, current, saved),
          reviewedValue: runSummary?.reviewedValues ? runSummary.reviewedValues[item.field] : undefined,
          legacy: !runSummary?.reviewedValues,
        },
      ])
  );

// Form field names whose AI review field is called differently.
const AI_REVIEW_FIELD: Record<string, string> = {
  foodlaCategory: 'category',
};

export const useAiFieldVerdict = (field?: string) => {
  const verdicts = React.useContext(AiFieldVerdictContext);
  if (!field) return undefined;
  return verdicts[field] || verdicts[AI_REVIEW_FIELD[field]];
};

const useStyles = makeStyles(theme => ({
  // Purple, not the red of form errors: this is the AI review's opinion, not a validation error.
  verdict: {
    marginTop: theme.spacing(0.75),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(0.75, 1.25),
    borderLeft: '3px solid #7b1fa2',
    backgroundColor: 'rgba(123, 31, 162, 0.06)',
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.8125rem',
    lineHeight: 1.5,
    color: theme.palette.text.primary,
    overflowWrap: 'anywhere',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(0.5),
  },
  rowIcon: {
    color: '#7b1fa2',
    fontSize: 20,
    marginLeft: theme.spacing(1),
    cursor: 'help',
    alignSelf: 'center',
    flexShrink: 0,
  },
  // The tooltip holds the whole verdict, so it is wide and light like the block under a field.
  iconTooltip: {
    maxWidth: 520,
    padding: 0,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[4],
  },
  heading: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontWeight: 600,
    color: '#7b1fa2',
    cursor: 'help',
  },
  icon: {
    fontSize: 16,
  },
  // Changed in the form since the review: still shown, faded and struck through until saved.
  changed: {
    opacity: 0.65,
  },
  changedIcon: {
    opacity: 0.45,
  },
  legacy: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    cursor: 'help',
  },
}));

// Saved changes hide the verdict under the field; the AI panel says the field changed.
export const UNSAVED_CHANGE_HINT = (reviewedValue: unknown) =>
  `Changed after the AI review and not saved yet. This verdict is about the value the review saw: ` +
  `“${describeReviewedValue(reviewedValue)}”. Once saved, it is no longer shown here, and the AI panel says the ` +
  'field changed. The AI has not checked the new value.';

export const LEGACY_REVIEW_HINT =
  'An older AI review, from before reviews were logged. It may be out of date: whether this field changed ' +
  'since the review cannot be tracked. Review the product again to be sure.';

const LEGACY_UNSAVED_CHANGE_HINT =
  'Changed in the form and not saved yet; the AI has not checked the new value. ' + LEGACY_REVIEW_HINT;

// What the AI review said about a failed field, under the field itself.
const SOURCE_HINT =
  'From the AI review of this product, not a check of the form: the AI failed this field for the reason below. ' +
  'Open the AI panel (robot button) for the whole review and its details. Only superusers see this.';

// The verdict itself: a heading line, then the reason points and the findings on lines of their own.
const VerdictContent = ({ review }: { review: IAiFieldVerdict }) => {
  const classes = useStyles();
  return (
    <>
      <div className={classes.headerRow}>
        <Tooltip arrow enterTouchDelay={0} title={SOURCE_HINT}>
          <span className={classes.heading}>
            <SmartToyIcon className={classes.icon} />
            AI review · failed
          </span>
        </Tooltip>
        {typeof review.confidence === 'number' && <ConfidenceChip confidence={review.confidence} provider={review.provider} />}
        {review.legacy && (
          <Tooltip arrow enterTouchDelay={0} title={LEGACY_REVIEW_HINT}>
            <span className={classes.legacy} data-testid="ai-field-verdict-legacy">
              Older review, may be out of date
            </span>
          </Tooltip>
        )}
      </div>
      <AiReviewReasonPoints
        reason={review.reason}
        findings={review.findings}
        empty={<div>Failed.</div>}
        struckHint={
          review.change !== 'unsaved'
            ? undefined
            : review.legacy
            ? LEGACY_UNSAVED_CHANGE_HINT
            : UNSAVED_CHANGE_HINT(review.reviewedValue)
        }
      />
    </>
  );
};

// What the AI review said about a failed field, under the field itself.
const AiFieldVerdict = ({ field }: { field?: string }) => {
  const classes = useStyles();
  const review = useAiFieldVerdict(field);
  if (!review || review.change === 'saved') return null;
  return (
    <div
      className={`${classes.verdict} ${review.change === 'unsaved' ? classes.changed : ''}`}
      data-testid="ai-field-verdict"
      data-field={review.field}
      data-change={review.change || undefined}
    >
      <VerdictContent review={review} />
    </div>
  );
};

// The same verdict as a small icon with the verdict in a tooltip, for number fields set in a row with
// their unit, where a block under the input would break the row.
export const AiFieldVerdictIcon = ({ field }: { field?: string }) => {
  const classes = useStyles();
  const review = useAiFieldVerdict(field);
  if (!review || review.change === 'saved') return null;
  return (
    <Tooltip
      arrow
      interactive
      enterTouchDelay={0}
      classes={{ tooltip: classes.iconTooltip }}
      title={
        <div className={classes.verdict} data-testid="ai-field-verdict" data-field={review.field} data-change={review.change || undefined}>
          {review.change === 'unsaved' && (
            <div className={classes.changed}>
              {review.legacy ? LEGACY_UNSAVED_CHANGE_HINT : UNSAVED_CHANGE_HINT(review.reviewedValue)}
            </div>
          )}
          <VerdictContent review={review} />
        </div>
      }
    >
      <SmartToyIcon className={`${classes.rowIcon} ${review.change === 'unsaved' ? classes.changedIcon : ''}`} aria-hidden={false} aria-label="AI review failed this field" data-testid="ai-field-verdict-icon" />
    </Tooltip>
  );
};

export default AiFieldVerdict;
